"use client";

import { useEffect, useRef } from "react";

import { CUSTOM_WHEEL_STRATEGY, type WheelStrategyKey } from "@/lib/wheel-strategies";

/* ---- Siri-style flowing wave signature (ported from the design source) ----
   Each strategy is an animated wave-function ribbon: calmer strategies drift
   slowly with cool palettes, aggressive ones churn faster and hotter. */
export type WaveKind = "custom" | "steady" | "wheeler" | "premium";

export const WAVE_KIND_BY_KEY: Record<WheelStrategyKey, WaveKind> = {
  [CUSTOM_WHEEL_STRATEGY]: "custom",
  STEADY_INCOME: "steady",
  WHEELER: "wheeler",
  PREMIUM_HUNTER: "premium",
};

export function waveKindForStrategy(key: WheelStrategyKey | undefined): WaveKind {
  if (!key) return "custom";
  return WAVE_KIND_BY_KEY[key] ?? "custom";
}

const WAVE_CONFIG: Record<WaveKind, { cols: string[]; energy: number; seed: number }> = {
  steady: { cols: ["#5eead4", "#34d399", "#0d9488", "#0f766e"], energy: 0.1, seed: 10293 },
  wheeler: { cols: ["#bef264", "#a3e635", "#65a30d", "#3f6212"], energy: 0.22, seed: 48571 },
  premium: { cols: ["#fde047", "#fbbf24", "#f59e0b", "#fb7185"], energy: 0.4, seed: 91237 },
  custom: { cols: ["#7dd3fc", "#818cf8", "#34d399", "#c084fc"], energy: 0.22, seed: 33667 },
};

type WaveLayer = {
  col: string;
  freq: number;
  amp: number;
  spd: number;
  yoff: number;
  phase: number;
  f2: number;
  a2: number;
  swell: number;
};

function makeRng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function buildWaves(kind: WaveKind): WaveLayer[] {
  const cfg = WAVE_CONFIG[kind];
  const e = cfg.energy;
  const r = makeRng(cfg.seed);
  const layers = 3 + Math.round(e * 1.5);
  const waves: WaveLayer[] = [];
  for (let i = 0; i < layers; i++) {
    waves.push({
      col: cfg.cols[i % cfg.cols.length],
      freq: 1.0 + r() * 1.6 + i * 0.35,
      amp: (0.1 + r() * 0.1) * (0.55 + e),
      spd: (0.35 + r() * 0.5) * (0.5 + e),
      yoff: (i - (layers - 1) / 2) * 0.13,
      phase: r() * 6.28,
      f2: 1.6 + r() * 1.6,
      a2: 0.25 + r() * 0.4,
      swell: 0.6 + r() * 0.8,
    });
  }
  return waves;
}

function hexA(hex: string, a: number) {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

function drawWaves(ctx: CanvasRenderingContext2D, W: number, H: number, waves: WaveLayer[], t: number) {
  const cx = W / 2;
  const cy = H / 2;
  const R = Math.min(W, H) * 0.47;
  const tt = t * 0.001;
  ctx.clearRect(0, 0, W, H);

  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.clip();

  const base = ctx.createRadialGradient(cx, cy, R * 0.05, cx, cy, R);
  base.addColorStop(0, "#0d1316");
  base.addColorStop(1, "#070a0c");
  ctx.fillStyle = base;
  ctx.fillRect(cx - R, cy - R, R * 2, R * 2);

  const steps = 64;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.globalCompositeOperation = "lighter";
  for (const w of waves) {
    const swell = 0.78 + 0.22 * Math.sin(tt * w.swell * 0.6 + w.phase);
    const pts: Array<[number, number]> = [];
    for (let s = 0; s <= steps; s++) {
      const u = s / steps;
      const xx = (u - 0.5) * 2;
      const win = Math.sqrt(Math.max(0, 1 - xx * xx));
      const wave =
        Math.sin(w.freq * xx * Math.PI + tt * w.spd + w.phase) * 0.7 +
        w.a2 * Math.sin(w.freq * w.f2 * xx * Math.PI + tt * w.spd * 0.7 + w.phase * 1.3) * 0.3;
      const y = cy + R * (w.yoff + w.amp * swell * wave * win);
      pts.push([cx + xx * R, y]);
    }
    const grad = ctx.createLinearGradient(0, cy - R * 0.5, 0, cy + R * 0.7);
    grad.addColorStop(0, hexA(w.col, 0.16));
    grad.addColorStop(1, hexA(w.col, 0));
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let s = 1; s < pts.length; s++) ctx.lineTo(pts[s][0], pts[s][1]);
    ctx.lineTo(pts[pts.length - 1][0], cy + R);
    ctx.lineTo(pts[0][0], cy + R);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (let s = 1; s < pts.length; s++) ctx.lineTo(pts[s][0], pts[s][1]);
    ctx.lineWidth = Math.max(1, R * 0.022);
    ctx.strokeStyle = hexA(w.col, 0.7);
    ctx.stroke();
  }
  ctx.globalCompositeOperation = "source-over";

  const shade = ctx.createRadialGradient(cx, cy, R * 0.3, cx, cy, R * 1.02);
  shade.addColorStop(0, "rgba(0,0,0,0)");
  shade.addColorStop(1, "rgba(0,0,0,0.38)");
  ctx.fillStyle = shade;
  ctx.fillRect(cx - R, cy - R, R * 2, R * 2);
  ctx.restore();

  ctx.lineWidth = Math.max(1, R * 0.012);
  ctx.strokeStyle = "rgba(255,255,255,0.10)";
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.stroke();
}

export function StrategyWaves({ kind, size }: { kind: WaveKind; size: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const waves = buildWaves(kind);
    const W = canvas.width;
    const H = canvas.height;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    const render = (t: number) => {
      drawWaves(ctx, W, H, waves, t);
      if (!reduce) raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [kind]);
  return (
    <canvas
      ref={ref}
      width={size * 2}
      height={size * 2}
      style={{ width: size, height: size }}
      className="block drop-shadow-[0_6px_14px_rgba(0,0,0,0.45)]"
    />
  );
}
